import React, { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { message, Upload, Input, Button, Image, Select } from "antd";
import { Helmet } from "react-helmet";
import { Layout } from "../pages/app/Layout";
import { Loader } from "../components/Loader";
import { PBox, PH4, PImg, PText, PH2, PH1 } from "../theme/BaseTheme";
import { refetchUser } from "../../actions/auth";
import ChangePlanModal from "../components/app/ChangePlanModal";
import PaymentConfirmModal from "../components/app/PaymentConfirmModal";
import { SellFaq } from "../sections/SellFaq";
import { linkedin_headshot_generator_faq_data } from "./LinkedinHeadshotLanding";
import { generateLinkedinHeadshot } from "../../actions/linkedinHeadshot";
import LinkedinHeadshotDownloadModal from "./LinkedinHeadshotDownloadModal";
import { UploadErrorMessages } from "../../data/messages";

const getBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });

const backgroundOptions = [
    {
        value: "grey",
        label: "Grey",
        image: "/images/linkedin-headshot/grey.webp",
    },
    {
        value: "white",
        label: "White",
        image: "/images/linkedin-headshot/white.webp",
    },
    {
        value: "brown",
        label: "Brown",
        image: "/images/linkedin-headshot/brown.webp",
    },
    { value: "red", label: "Red", image: "/images/linkedin-headshot/red.webp" },
    {
        value: "blue",
        label: "Blue",
        image: "/images/linkedin-headshot/blue.webp",
    },
    {
        value: "indigo",
        label: "Indigo",
        image: "/images/linkedin-headshot/indigo.webp",
    },
    {
        value: "golden",
        label: "Golden",
        image: "/images/linkedin-headshot/golden.webp",
    },
    {
        value: "beige",
        label: "Beige",
        image: "/images/linkedin-headshot/beige.webp",
    },
    {
        value: "street",
        label: "Street",
        image: "/images/linkedin-headshot/street.webp",
    },
    {
        value: "office",
        label: "Office",
        image: "/images/linkedin-headshot/office.webp",
    },
    {
        value: "coffee shop",
        label: "Coffee Shop",
        image: "/images/linkedin-headshot/coffee-shop.webp",
    },
    {
        value: "park",
        label: "Park",
        image: "/images/linkedin-headshot/park.webp",
    },

    {
        value: "study",
        label: "Study",
        image: "/images/linkedin-headshot/study.webp",
    },
    {
        value: "skyscraper",
        label: "Skyscraper",
        image: "/images/linkedin-headshot/skyscraper.webp",
    },
    {
        value: "seaside",
        label: "Seaside",
        image: "/images/linkedin-headshot/seaside.webp",
    },
    {
        value: "dinner",
        label: "Dinner",
        image: "/images/linkedin-headshot/dinner.webp",
    },
    {
        value: "waterfront",
        label: "Waterfront",
        image: "/images/linkedin-headshot/waterfront.webp",
    },
    {
        value: "sunlight",
        label: "Sunlight",
        image: "/images/linkedin-headshot/sunlight.webp",
    },
];

const poseOptions = [
    { value: "straight on", label: "Straight On" },
    { value: "1/4 turn", label: "1/4 Turn" },
    { value: "lean forward", label: "Lean Forward" },
    { value: "arms crossed", label: "Arms Crossed" },
    { value: "chin touch", label: "Chin Touch" },
];

class LinkedinHeadshotApp extends Component {
    canvasRef = React.createRef();

    constructor(props) {
        super(props);
        this.highlightRef = React.createRef();
        this.resultRef = React.createRef();
        this.state = {
            isLoading: true,
            brushSize: 30,
            isGenerating: false,
            isDownloading: false,
            uploadedImage: "",
            linkedinHeadshot: "",
            prompt: "grey",
            fileList: [],
            isChangePlanModalOpen: false,
            isPaymentConfirmModalOpen: false,
            newPlan: "",
            shouldScrollToResult: false,
            isDownloadModalOpen: false,
            background: "grey",
            pose: "straight on",
        };
        this.handleUpload = this.handleUpload.bind(this);
        this.handleDownloadModalOk = this.handleDownloadModalOk.bind(this);
        this.handleDownloadModalCancel =
            this.handleDownloadModalCancel.bind(this);
    }

    handleUpload = async ({ file }) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj);
        }
        await this.setState({
            uploadedImage: file.url || file.preview,
        });
    };

    async componentDidMount() {
        await this.setState({ isLoading: false });
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.state.shouldScrollToResult && this.highlightRef.current) {
            this.highlightRef.current.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
            this.setState({ shouldScrollToResult: false });
        }
    }

    handleDownloadModalOk = () => {
        this.setState({ isDownloadModalOpen: false });
    };

    handleDownloadModalCancel = () => {
        this.setState({ isDownloadModalOpen: false });
    };

    render() {
        if (this.state.isLoading) {
            return <Loader />;
        }
        return (
            <Layout>
                <Helmet>
                    <title>LinkedIn Headshot Generator App</title>
                    <meta
                        name="description"
                        content="AI-powered linkedin headshot generator to generate linkedin headshots for your profile."
                    />
                    <meta
                        name="keywords"
                        content="Linkedin Headshot Generator, AI Linkedin Headshot Generator, Linkedin Headshot Generator APP, Linkedin Headshot Online Generator, Image Processing, AI Image Editing, Patternful, Patternful AI"
                    />
                </Helmet>
                <PBox css={styles}>
                    <PBox className="app-content">
                        <PBox className="app-content-title">
                            <PH1>AI LinkedIn Headshot Generator</PH1>
                            <PText>
                                Create a professional headshot for your LinkedIn
                                profile.{" "}
                            </PText>
                        </PBox>
                        <PBox className="app-content-control">
                            <PBox className="linkedin-headshot-upload-image">
                                <PBox className="upload-section">
                                    <PBox className="linkedin-headshot-upload">
                                        <Upload.Dragger
                                            onChange={async (info) => {
                                                await this.handleUpload(info);
                                            }}
                                            showUploadList={false}
                                            listType="picture-card"
                                            maxCount={1}
                                            beforeUpload={async (file) => {
                                                const isJpgOrPng =
                                                    file.type ===
                                                        "image/jpeg" ||
                                                    file.type === "image/png";
                                                if (!isJpgOrPng) {
                                                    message.error(
                                                        UploadErrorMessages.JPG_OR_PNG
                                                    );
                                                }
                                                const isLt4M =
                                                    file.size / 1024 / 1024 < 4;
                                                if (!isLt4M) {
                                                    message.error(
                                                        UploadErrorMessages.IMAGE_SIZE_4MB
                                                    );
                                                }
                                                return (
                                                    (isJpgOrPng && isLt4M) ||
                                                    Upload.LIST_IGNORE
                                                );
                                            }}
                                        >
                                            {this.state.uploadedImage !==
                                                "" && (
                                                <PImg
                                                    className="background-removed-image"
                                                    src={
                                                        this.state.uploadedImage
                                                    }
                                                />
                                            )}
                                            {this.state.uploadedImage ===
                                                "" && (
                                                <PText>
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="#000000"
                                                        viewBox="0 0 256 256"
                                                    >
                                                        <path d="M224,152v56a16,16,0,0,1-16,16H48a16,16,0,0,1-16-16V152a8,8,0,0,1,16,0v56H208V152a8,8,0,0,1,16,0ZM93.66,85.66,120,59.31V152a8,8,0,0,0,16,0V59.31l26.34,26.35a8,8,0,0,0,11.32-11.32l-40-40a8,8,0,0,0-11.32,0l-40,40A8,8,0,0,0,93.66,85.66Z"></path>
                                                    </svg>
                                                    <br />
                                                    Click to upload your image,
                                                    <br />
                                                    or drag and drop your image
                                                    into this area.
                                                </PText>
                                            )}
                                        </Upload.Dragger>
                                    </PBox>
                                </PBox>
                                <PBox className="input-section">
                                    <PText className="control-section-label">
                                        Pose
                                    </PText>
                                    <Select
                                        style={{
                                            width: "100%",
                                        }}
                                        options={poseOptions}
                                        onChange={async (value) => {
                                            this.setState({
                                                pose: value,
                                            });
                                        }}
                                        value={this.state.pose}
                                    />
                                    <PText className="control-section-label">
                                        Background
                                    </PText>
                                    <div className="background-options-grid">
                                        {backgroundOptions.map((option) => (
                                            <div
                                                key={option.value}
                                                className={`background-option ${
                                                    this.state.background ===
                                                    option.value
                                                        ? "selected"
                                                        : ""
                                                }`}
                                                onClick={() => {
                                                    this.setState({
                                                        background:
                                                            option.value,
                                                        prompt: option.value,
                                                    });
                                                }}
                                            >
                                                <img
                                                    src={option.image}
                                                    alt={option.label}
                                                />
                                                <span>{option.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                </PBox>
                            </PBox>
                            <PBox className="generate-button-container">
                                <Button
                                    type="primary"
                                    onClick={async () => {
                                        if (
                                            this.props.userAccount?.credits +
                                                this.props.userAccount
                                                    ?.roll_over_credits <
                                            5
                                        ) {
                                            message.error(
                                                "Your credit balance is not enough. Please consider upgrading your plan or buying more credits.",
                                                3
                                            );
                                        } else {
                                            if (
                                                this.state.uploadedImage === ""
                                            ) {
                                                message.error(
                                                    "Please upload a headshot image. ",
                                                    3
                                                );
                                            } else if (
                                                this.state.prompt === ""
                                            ) {
                                                message.error(
                                                    "Prompt cannot be empty. ",
                                                    3
                                                );
                                            } else {
                                                await this.setState({
                                                    isGenerating: true,
                                                });
                                                message.open({
                                                    key: "gen_linkedin_headshot",
                                                    type: "loading",
                                                    content:
                                                        "Start generating linkedin headshots. This can take a while.",
                                                    duration: 0,
                                                });
                                                await this.props.generateLinkedinHeadshot(
                                                    this.state.uploadedImage,
                                                    this.state.prompt,
                                                    this.state.pose
                                                );
                                                message.destroy(
                                                    "gen_linkedin_headshot"
                                                );
                                                await this.props.refetchUser();
                                                await this.setState({
                                                    isGenerating: false,
                                                    shouldScrollToResult: true,
                                                });
                                            }
                                        }
                                    }}
                                    loading={this.state.isGenerating}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    Generate{" "}
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="#000000"
                                        viewBox="0 0 256 256"
                                        style={{
                                            fill: "#d48806",
                                            backgroundColor: "#ffe58f",
                                            padding: "0.125rem 0.25rem",
                                            borderRadius: "0.25rem",
                                            width: "1rem",
                                            height: "1rem",
                                            marginLeft: "0.5rem",
                                            marginRight: "0.25rem",
                                        }}
                                    >
                                        <path d="M200,48H136V16a8,8,0,0,0-16,0V48H56A32,32,0,0,0,24,80V192a32,32,0,0,0,32,32H200a32,32,0,0,0,32-32V80A32,32,0,0,0,200,48Zm16,144a16,16,0,0,1-16,16H56a16,16,0,0,1-16-16V80A16,16,0,0,1,56,64H200a16,16,0,0,1,16,16Zm-52-56H92a28,28,0,0,0,0,56h72a28,28,0,0,0,0-56Zm-28,16v24H120V152ZM80,164a12,12,0,0,1,12-12h12v24H92A12,12,0,0,1,80,164Zm84,12H152V152h12a12,12,0,0,1,0,24ZM72,108a12,12,0,1,1,12,12A12,12,0,0,1,72,108Zm88,0a12,12,0,1,1,12,12A12,12,0,0,1,160,108Z"></path>
                                    </svg>
                                    x5
                                </Button>
                                <Button
                                    type="ghost"
                                    href="/linkedin-headshots/1"
                                >
                                    View My Headshots
                                </Button>
                            </PBox>

                            {this.props.linkedinHeadshot !== undefined && (
                                <>
                                    <PBox
                                        className="generate-linkedin-headshot-content"
                                        ref={this.highlightRef}
                                    >
                                        <PBox className="binary-mask-sketch-canva">
                                            <Image
                                                src={
                                                    this.props.linkedinHeadshot
                                                }
                                                crossOrigin={
                                                    process.env.NODE_ENV ===
                                                    "development"
                                                        ? "anonymous"
                                                        : ""
                                                }
                                            />
                                        </PBox>
                                    </PBox>{" "}
                                    <PBox className="generate-button-container">
                                        <Button
                                            type="primary"
                                            onClick={async () => {
                                                await this.setState({
                                                    isDownloadModalOpen: true,
                                                });
                                            }}
                                        >
                                            Download
                                        </Button>
                                    </PBox>
                                </>
                            )}

                            <SellFaq
                                data={linkedin_headshot_generator_faq_data}
                            />
                        </PBox>
                        {this.state.isChangePlanModalOpen && (
                            <ChangePlanModal
                                isChangePlanModalOpen={
                                    this.state.isChangePlanModalOpen
                                }
                                handleChangePlanModalOk={async () => {
                                    await this.setState({
                                        isPaymentConfirmModalOpen: true,
                                    });
                                    await this.setState({
                                        isChangePlanModalOpen: false,
                                    });
                                }}
                                handleChangePlanModalCancel={() => {
                                    this.setState({
                                        isChangePlanModalOpen: false,
                                    });
                                }}
                                setNewPlan={(value) => {
                                    this.setState({ newPlan: value });
                                }}
                            />
                        )}
                        {this.state.isDownloadModalOpen && (
                            <LinkedinHeadshotDownloadModal
                                linkedinHeadshotUUID={this.props.uuid}
                                isDownloadModalOpen={
                                    this.state.isDownloadModalOpen
                                }
                                handleDownloadModalOk={
                                    this.handleDownloadModalOk
                                }
                                handleDownloadModalCancel={
                                    this.handleDownloadModalCancel
                                }
                            />
                        )}
                        {this.state.isPaymentConfirmModalOpen && (
                            <PaymentConfirmModal
                                newPlan={this.state.newPlan}
                                isPaymentConfirmModalOpen={
                                    this.state.isPaymentConfirmModalOpen
                                }
                                handlePaymentConfirmModalOk={() =>
                                    this.setState({
                                        isPaymentConfirmModalOpen: false,
                                    })
                                }
                                handlePaymentConfirmModalCancel={() =>
                                    this.setState({
                                        isPaymentConfirmModalOpen: false,
                                    })
                                }
                            />
                        )}
                    </PBox>
                </PBox>
            </Layout>
        );
    }
}

const styles = {
    minHeight: "48rem",
    "& .app-content-title": {
        "& h1": {
            textAlign: "center",
        },
        "& p": {
            textAlign: "center",
        },
        margin: "4rem auto",
    },
    "& .app-content-control": {
        margin: "0 auto",
    },
    "& .generate-button-container": {
        display: "flex",
        justifyContent: "center",
        margin: "2rem 0",
        "& button": {
            margin: "0 0.5rem",
        },
    },
    "& .linkedin-headshot-upload-image": {
        margin: "2rem auto 2rem auto",
        maxWidth: "64rem",
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "2rem",
        alignItems: "start",
        "@media (max-width: 768px)": {
            gridTemplateColumns: "1fr",
            gap: "1rem",
        },

        "& .upload-section": {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
        },
        "& .input-section": {
            display: "flex",
            flexDirection: "column",
            "& .control-section-label": {
                fontWeight: "600",
                fontSize: "14px",
                color: "$colors$text",
                marginBottom: "1rem",
            },
            "& .background-options-grid": {
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                gap: "1rem",
                height: "240px",
                overflowY: "auto",
                padding: "0.5rem",
                border: "1px solid $colors$borderColor",
                borderRadius: "8px",
                "&::-webkit-scrollbar": {
                    width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                    background: "#f1f1f1",
                    borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                    background: "#888",
                    borderRadius: "4px",
                    "&:hover": {
                        background: "#555",
                    },
                },
                "& .background-option": {
                    cursor: "pointer",
                    textAlign: "center",
                    transition: "all 0.2s ease",
                    borderRadius: "8px",
                    padding: "0.5rem",
                    border: "2px solid transparent",
                    "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    },
                    "&.selected": {
                        border: "2px solid $colors$primary",
                        backgroundColor: "rgba(24, 144, 255, 0.1)",
                    },
                    "& img": {
                        width: "100%",
                        height: "80px",
                        objectFit: "contain",
                        borderRadius: "4px",
                        marginBottom: "0.5rem",
                    },
                    "& span": {
                        display: "block",
                        fontSize: "12px",
                        color: "$colors$text",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    },
                },
            },
        },
        "& .linkedin-headshot-upload": {
            marginBottom: "1rem",
            width: "100%",
            "& svg": {
                width: "3rem",
                height: "3rem",
                fill: "$colors$primary",
            },
            "& span": {
                padding: "0",
                width: "100%",
                height: "24rem",
                display: "block",
                margin: "0",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
            },
        },
        "& .background-removed-image": {
            maxWidth: "100%",
            maxHeight: "14rem",
            margin: "auto 1rem",
            objectFit: "contain",
        },
        "& h2": {
            marginBottom: "3rem",
        },
    },
    "& .remove-background-download-buttons": {
        margin: "5rem auto",
        display: "flex",
        justifyContent: "center",
        "& button": {
            margin: "0 0.5rem",
        },
    },
    "& .remove-background-content": {
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: "2rem",
        marginTop: "8rem",
        marginBottom: "4rem",
        "& button": {
            width: "100%",
        },
    },
    "& .binary-mask-sketch-canva": {
        display: "block",
        "& img": {
            display: "block",
            margin: "auto",
            width: `calc(31rem - 2px)`,
        },
    },
    "& .binary-mask-brush-size": {
        textAlign: "left",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: "1rem",
        "& h4": { marginLeft: "0.5rem" },
    },
    "& .binary-mask-result-image": {
        margin: "6rem auto 0 auto",
        "& img": {
            height: "40rem",
            margin: "0 auto",
        },
    },
    "& .upscale-option": {
        display: "flex",
        alignItems: "center",
    },
    "& .caption-before": {
        background: "#fff",
        border: "1px solid #c0c0c0",
        borderRadius: "12px",
        color: "#2e3452",
        opacity: "0.8",
        padding: "12px",
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        lineHeight: "100%",
        left: "12px",
    },
    "& .caption-after": {
        background: "#fff",
        border: "1px solid #c0c0c0",
        borderRadius: "12px",
        color: "#2e3452",
        opacity: "0.8",
        padding: "12px",
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        lineHeight: "100%",
        right: "12px",
    },
    "& .slider-with-animated-handle": {
        position: "relative",
        margin: "auto auto",
        //"& img": { maxHeight: "28rem" },
    },
    "& .remove-background-slider": {
        width: "60rem",
        height: "40rem",
        border: "1px solid $colors$borderColor",
        position: "relative",
        borderRadius: "0.5rem",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        margin: "0 auto",
        "& img": {
            //width: "30rem",
            maxHeight: "40rem",
            margin: "0 auto",
        },
    },
    "& .generate-linkedin-headshot-content": {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        margin: "8rem auto 2rem auto",
        "& img": {
            maxHeight: "40rem",
        },
    },
};

function mapStateToProps(state) {
    return {
        userAccount: state.authReducer?.userAuth?.userAccount,
        linkedinHeadshot:
            state.linkedinHeadshotReducer?.generateLinkedinHeadshot
                ?.generatedImage,
        uuid: state.linkedinHeadshotReducer?.generateLinkedinHeadshot?.uuid,
    };
}

export default connect(mapStateToProps, {
    refetchUser,
    generateLinkedinHeadshot,
})(withRouter(LinkedinHeadshotApp));
